// Failure class display (2026-10-14 failure-diagnostics; plan
// internal/compiler/plans/2026-10-14-failure-diagnostics.md).
//
// The runner classifies every failed job (internal/api shared/failureClass.ts
// is the canonical classifier + vocabulary); the broker returns
// failure_class / failure_cause / bundle_key on the job list and detail rows.
// This file turns those fields into what the pages render: a chip (label +
// tone) and the one-line cause. The label/tone table mirrors the canonical
// FAILURE_CLASS_TABLE — failureClass.test.ts compares them whenever the
// sibling internal/api checkout is present. An id this build has never heard
// of (a class added after this deploy) still renders, as a neutral chip with
// its raw id.

export type FailureTone = "destructive" | "warning" | "secondary" | "info";

export const FAILURE_CLASS_LABELS: Record<
  string,
  { label: string; tone: FailureTone; summary: string }
> = {
  "oom-llvm": {
    label: "OOM (LLVM)",
    tone: "destructive",
    summary: "rustc/LLVM ran out of memory",
  },
  "oom-alloc": {
    label: "OOM (alloc)",
    tone: "destructive",
    summary: "a process aborted on a failed allocation",
  },
  "oom-kernel": {
    label: "OOM (kernel)",
    tone: "destructive",
    summary: "the OOM killer killed a build process",
  },
  "rustc-signal": {
    label: "rustc signal",
    tone: "destructive",
    summary: "rustc died on a signal",
  },
  "rustc-ice": {
    label: "rustc ICE",
    tone: "destructive",
    summary: "internal compiler error",
  },
  "rustc-error": {
    label: "rustc error",
    tone: "warning",
    summary: "generated Rust failed to compile",
  },
  "linker-error": {
    label: "linker error",
    tone: "warning",
    summary: "the final link failed",
  },
  "cargo-resolve": {
    label: "cargo/network",
    tone: "warning",
    summary: "dependency resolution / download failed",
  },
  "transpile-error": {
    label: "transpile error",
    tone: "warning",
    summary: "the Pure transpiler rejected the input",
  },
  "compile-timeout": {
    label: "timeout",
    tone: "warning",
    summary: "the build exceeded its time limit",
  },
  "vm-boot": {
    label: "VM boot",
    tone: "destructive",
    summary: "the build VM failed to boot",
  },
  "guest-died": {
    label: "guest died",
    tone: "destructive",
    summary: "the build VM died mid-build",
  },
  "admission-timeout": {
    label: "admission timeout",
    tone: "secondary",
    summary: "waited too long for capacity",
  },
  cancelled: {
    label: "cancelled",
    tone: "secondary",
    summary: "cancelled by an operator",
  },
  "pgo-profile-missing": {
    label: "PGO profile",
    tone: "warning",
    summary: "the PGO profile was missing or unusable",
  },
  "runner-lost": {
    label: "runner lost",
    tone: "destructive",
    summary: "the runner stopped reporting",
  },
  "runner-unavailable": {
    label: "runner unavailable",
    tone: "destructive",
    summary: "the runner could not take the job",
  },
  "invalid-request": {
    label: "invalid request",
    tone: "secondary",
    summary: "the job request was rejected",
  },
  unknown: {
    label: "unknown",
    tone: "secondary",
    summary: "no recognised failure signature",
  },
};

/** The failure fields of a job row (list or detail). */
export interface FailureFields {
  status?: string;
  failure_class?: string | null;
  failure_cause?: string | null;
  bundle_key?: string | null;
  /** The verdict was computed on read (older job), not stored by the runner. */
  failure_backfilled?: boolean;
}

export interface FailureView {
  id: string;
  label: string;
  tone: FailureTone;
  /** One-line cause, "" when there is none. */
  cause: string;
  /** Hover text: what the class means, and where the verdict came from. */
  title: string;
  hasBundle: boolean;
  backfilled: boolean;
}

/** null for a job with no verdict worth showing (succeeded/running/queued, or
 *  a failed job nothing could classify yet). */
export function failureView(job: FailureFields): FailureView | null {
  if (job.status && job.status !== "failed" && job.status !== "cancelled")
    return null;
  const id = job.failure_class;
  if (!id) return null;
  const known = FAILURE_CLASS_LABELS[id];
  const backfilled = job.failure_backfilled === true;
  return {
    id,
    label: known?.label ?? id,
    tone: known?.tone ?? "secondary",
    cause: (job.failure_cause ?? "").trim(),
    title: `${known?.summary ?? `failure class ${id}`}${
      backfilled
        ? " — classified now from the stored log (the runner stored no verdict for this older job)"
        : ""
    }`,
    hasBundle: !!job.bundle_key,
    backfilled,
  };
}

/** Stable order for per-class counts / filters: the vocabulary order. */
export function failureClassOrder(): string[] {
  return Object.keys(FAILURE_CLASS_LABELS);
}
